/*
 * Clone My Voice — record a 1–3 min sample, we build an ElevenLabs
 * clone, and new installs speak every un-recorded line in your voice.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import SoulumoHeader from "../../components/SoulumoHeader";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Sparkles, Trash2, RotateCcw } from "lucide-react";
import { TOK, SERIF, SANS, Orb, NAVY_BG } from "./SignatureBits";
import { CloneRecorder } from "../../components/voiceClone/CloneRecorder";
import { SoundCheck } from "../../components/voiceClone/SoundCheck";
import { REDO_MIN_SECONDS } from "../../components/voiceClone/cloneScript";
import { CloneCompare } from "../../components/voiceClone/CloneCompare";
import { VoiceGiftComposer } from "../../components/voiceClone/VoiceGiftComposer";
import { RevoicePending } from "../../components/soulumoCreate/RevoiceButton";
import { CloneSubscribe, ManageBilling, PremiumCard, PremiumBadge } from "../../components/voiceClone/CloneSubscribe";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const ORB = "radial-gradient(circle at 50% 50%, #fff1c9 0%, rgba(215,187,122,0.8) 22%, rgba(215,187,122,0.2) 48%, rgba(13,31,58,0) 68%)";

const Note = ({ children, testId }) => (
  <p className="text-[1.05rem] italic leading-relaxed" style={{ color: TOK.creamDim, fontFamily: SERIF }} data-testid={testId}>{children}</p>
);

const ReadyState = ({ me, onRedo, onRemove, busy }) => (
  <div className="space-y-6" data-testid="clone-ready">
    <div className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.28em]" style={{ color: TOK.gold, fontFamily: SANS }}>
      <Sparkles size={12} /> Your voice is ready
    </div>
    <Note>From now on, every new installation you create speaks the lines you haven&apos;t recorded in your own cloned voice. Lines you recorded yourself still play exactly as you said them.</Note>
    <SoundCheck onRedo={onRedo} voiceId={me.voice_id || me.ready_at} />
    <p className="text-xs" style={{ color: TOK.creamMist }}>Sample · {Math.round(me.sample_seconds || 0)}s · created {(me.ready_at || me.created_at || "").slice(0, 10)}</p>
    <div className="flex gap-3 flex-wrap">
      <button onClick={onRedo} data-testid="clone-redo-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-[0.65rem] uppercase tracking-[0.2em]" style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, minHeight: 44 }}>
        <RotateCcw size={13} /> Re-record sample
      </button>
      <button onClick={onRemove} disabled={busy} data-testid="clone-remove-btn" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-[0.65rem] uppercase tracking-[0.2em] disabled:opacity-40" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.creamMist, minHeight: 44 }}>
        <Trash2 size={13} /> Remove my voice
      </button>
    </div>
  </div>
);

const fmtDay = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long" });

const AllowanceNote = ({ a }) => {
  if (!a || !a.allowance) return null;
  return a.over_allowance ? (
    <p className="mt-8 rounded-2xl px-4 py-3 text-sm italic" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: SERIF }} data-testid="clone-allowance-over">
      You&apos;ve used this month&apos;s voice allowance. New installations will use the Soulumo narrator until your allowance resets on {fmtDay(a.resets_on)}.
    </p>
  ) : (
    <p className="mt-6 text-xs" style={{ color: TOK.creamMist }} data-testid="clone-allowance-usage">
      This month · {Math.round((100 * a.chars_month) / Math.max(1, a.allowance))}% of your voice allowance used (about 10–12 new personalised installations a month) · resets {fmtDay(a.resets_on)}
    </p>
  );
};

export default function VoiceClonePage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [redo, setRedo] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => axios.get(`${API}/api/voice-clone/me`, auth()).then((r) => setMe(r.data)).catch(() => setMe({ enabled: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (me?.status !== "processing") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [me?.status, load]);

  const remove = async () => {
    if (!window.confirm("Remove your cloned voice? New installations will use the Soulumo narrator again.")) return;
    setBusy(true);
    try { await axios.delete(`${API}/api/voice-clone/me`, auth()); await load(); } finally { setBusy(false); }
  };

  const showRecorder = me?.enabled && (redo || ["none", "failed"].includes(me.status));
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: NAVY_BG, backgroundAttachment: "fixed", color: TOK.cream, fontFamily: SANS }} data-testid="voice-clone-page">
      <SoulumoHeader />
      <Orb gradient={ORB} size={420} className="absolute -top-44 -right-40" style={{ opacity: 0.5 }} />
      <div className="relative max-w-2xl mx-auto px-5 pt-5 pb-28">
        <button onClick={() => nav("/my-installations")} aria-label="Back" data-testid="voice-clone-back-btn" className="inline-flex items-center justify-center rounded-full"
          style={{ width: 44, height: 44, background: "rgba(13,31,58,0.4)", border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}>
          <ChevronLeft size={18} />
        </button>
        <header className="mt-10 mb-10 sig-rise">
          <div className="text-[0.6rem] uppercase tracking-[0.34em] mb-3" style={{ color: TOK.goldSoft }}>Premium · Clone My Voice</div>
          <h1 className="text-4xl sm:text-5xl font-light leading-[1.05]" style={{ fontFamily: SERIF, color: TOK.cream }}>Hear it in your own voice</h1>
          <p className="mt-3 text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>Read a short passage once. Every installation after that can speak to you as you.</p>
        </header>

        {!me && <Loader2 className="animate-spin" color={TOK.gold} />}
        {me && !me.enabled && <CloneSubscribe onActivated={load} />}
        {me?.enabled && me.status === "failed" && !redo && (
          <p className="mb-6 text-sm" style={{ color: "#dc8b6c" }} data-testid="clone-failed">We couldn&apos;t build your voice from that sample. Please record it again in a quiet room.</p>
        )}
        {me?.enabled && me.status === "processing" && (
          <div className="flex items-center gap-3" data-testid="clone-processing">
            <Loader2 className="animate-spin" color={TOK.gold} size={18} />
            <Note>Creating your voice… this usually takes under a minute.</Note>
          </div>
        )}
        {me?.enabled && me.status === "ready" && !redo && (
          <>
            <RevoicePending />
            <ReadyState me={me} busy={busy} onRedo={() => setRedo(true)} onRemove={remove} />
            <AllowanceNote a={me.allowance} />
            <CloneCompare />
            <VoiceGiftComposer />
          </>
        )}
        {me && !me.premium && me.status !== "processing" && <PremiumCard onActivated={load} hasPlan={me.enabled && !me.comped} />}
        {me?.premium && <PremiumBadge />}
        {me?.can_manage_billing && !me.comped && <div className="mt-10"><ManageBilling /></div>}
        {showRecorder && (
          <>
            <Note>Find a quiet room, hold your phone about a hand&apos;s width away, and read at your natural, gentle pace. One to three minutes is perfect.</Note>
            <div className="mt-8"><CloneRecorder minSeconds={redo ? REDO_MIN_SECONDS : undefined} onUploaded={(d) => { setRedo(false); setMe(d); }} /></div>
          </>
        )}
      </div>
    </div>
  );
}
